(() => {
    'use strict';

    /* ------------------------------------------------------------------
     * Image lightbox.
     *
     * Covers the content images on a project page and the project thumbnails
     * wherever they appear — the archive grid and the two featured projects on
     * the front page. The images live in blocks shipped by the monadius-blocks
     * plugin, so the affordance is built here at runtime rather than by editing
     * those templates: each image is wrapped in a button that carries the hover
     * veil and the always-visible expand badge. That also means an image only
     * becomes clickable when the script actually runs — no dead controls
     * without JS.
     *
     * The set the arrows walk is recomputed every time the dialog opens, from
     * whatever is on screen at that moment. On the archive the toolbar hides
     * filtered-out cards, and stepping into a project the visitor has just
     * filtered away would be a small betrayal.
     *
     * Exposed as MB.lightbox.init because swup replaces the page's images
     * without a reload. Re-running it wraps only what is not wrapped yet, and
     * rebuilds the gallery from the buttons still in the document — the ones
     * belonging to the page just left went out with it.
     * ---------------------------------------------------------------- */

    const root = document.documentElement;
    const stillness = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Labels are read out of the page rather than from wp_localize_script, which prints
    // once per document: under swup a Dutch page's labels would otherwise survive into an
    // English one. This element sits inside the swapped container, so it travels with it.
    const label = (key, fallback) => {
        const host = document.querySelector('[data-mb-lightbox-labels]');
        return (host && host.dataset[key]) || fallback;
    };

    // img.mb-lightbox is the opt-in a block's "Vergroten bij klik" option adds. The two
    // selectors before it are the original, implicit set — every content image on a project
    // page, and every project thumbnail — and stay as they were.
    const collect = () => Array.from(
        document.querySelectorAll('.single-post img.image, img.project-thumbnail, img.mb-lightbox')
    ).filter((img) => img.getAttribute('src') && !img.closest('.mb-zoom'));

    let items = [];
    let gallery = [];
    let current = 0;
    let lastFocused = null;
    let dialog = null;
    let frame = null;
    let counter = null;
    let closeBtn = null;
    let prevBtn = null;
    let nextBtn = null;

    // --- the zoom affordance around each image -----------------------------

    const wrap = (img) => {
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'mb-zoom';

        // The radius belongs to the image, but the veil sits on top of it and would show
        // square corners over a rounded picture.
        if (img.classList.contains('mb-rounded')) {
            trigger.classList.add('mb-rounded');
        }

        trigger.setAttribute('aria-label', label('enlarge', 'Enlarge image'));
        trigger.setAttribute('aria-haspopup', 'dialog');

        img.parentNode.insertBefore(trigger, img);
        trigger.appendChild(img);

        const veil = document.createElement('span');
        veil.className = 'mb-zoom-veil';
        veil.setAttribute('aria-hidden', 'true');
        trigger.appendChild(veil);

        const badge = document.createElement('span');
        badge.className = 'mb-zoom-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.innerHTML = '<i class="fa-solid fa-expand"></i>';
        trigger.appendChild(badge);

        trigger.addEventListener('click', () => {
            const item = items.find((entry) => entry.trigger === trigger);
            if (item) {
                open(item);
            }
        });
    };

    // --- the dialog --------------------------------------------------------

    const buildDialog = () => {
        dialog = document.createElement('div');
        dialog.className = 'mb-lightbox';
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
        dialog.hidden = true;

        // Every fragment below is a literal; the only values that ever come from the page
        // are the image src and alt, and those are assigned as properties further down.
        dialog.innerHTML = [
            '<div class="mb-lightbox-backdrop" data-mb-close></div>',
            '<div class="mb-lightbox-stage">',
            '<button type="button" class="mb-lightbox-btn mb-lightbox-close" data-mb-close>',
            '<i class="fa-solid fa-xmark" aria-hidden="true"></i></button>',
            '<button type="button" class="mb-lightbox-btn mb-lightbox-prev" data-mb-step="-1">',
            '<i class="fa-solid fa-chevron-left" aria-hidden="true"></i></button>',
            '<figure class="mb-lightbox-figure"><img class="mb-lightbox-image" alt=""></figure>',
            '<button type="button" class="mb-lightbox-btn mb-lightbox-next" data-mb-step="1">',
            '<i class="fa-solid fa-chevron-right" aria-hidden="true"></i></button>',
            '<p class="mb-lightbox-count" aria-live="polite"></p>',
            '</div>',
        ].join('');

        // Lives on <body>, outside the swapped container, so it is built once and is never
        // torn out from under an open dialog by a navigation.
        document.body.appendChild(dialog);

        frame = dialog.querySelector('.mb-lightbox-image');
        counter = dialog.querySelector('.mb-lightbox-count');
        closeBtn = dialog.querySelector('.mb-lightbox-close');
        prevBtn = dialog.querySelector('.mb-lightbox-prev');
        nextBtn = dialog.querySelector('.mb-lightbox-next');

        dialog.addEventListener('click', (event) => {
            if (event.target.closest('[data-mb-close]')) {
                close();
                return;
            }

            const stepper = event.target.closest('[data-mb-step]');
            if (stepper) {
                show(current + Number(stepper.dataset.mbStep));
            }
        });

        document.addEventListener('keydown', onKeydown);
    };

    // Refreshed on every init: after a language change the labels are different words.
    const labelDialog = () => {
        dialog.setAttribute('aria-label', label('enlarge', 'Enlarge image'));
        closeBtn.setAttribute('aria-label', label('close', 'Close'));
        prevBtn.setAttribute('aria-label', label('previous', 'Previous image'));
        nextBtn.setAttribute('aria-label', label('next', 'Next image'));
    };

    // offsetParent is null for anything inside a [hidden] card, which is exactly how the
    // archive toolbar filters — so this needs no knowledge of the toolbar at all.
    const onScreen = (item) => item.trigger.offsetParent !== null;

    const show = (index) => {
        current = (index + gallery.length) % gallery.length;

        const source = gallery[current].img;
        // data-full where there is one: the card thumbnail is a 640px crop, and blowing
        // that up to 640px is not an enlargement.
        frame.src = source.dataset.full || source.currentSrc || source.src;
        frame.alt = source.getAttribute('alt') || '';

        counter.textContent = (current + 1) + ' / ' + gallery.length;
    };

    function open(item) {
        // A thumbnail stands for a whole project, so it opens on its own: arrowing from
        // one project's cover to the next is browsing, which the page itself already does
        // better. Only the images within one project page form a set worth stepping through.
        gallery = item.solo ? [item] : items.filter((other) => !other.solo && onScreen(other));

        if (!gallery.length) {
            gallery = [item];
        }

        const many = gallery.length > 1;
        prevBtn.hidden = !many;
        nextBtn.hidden = !many;
        counter.hidden = !many;

        lastFocused = document.activeElement;
        show(Math.max(0, gallery.indexOf(item)));

        dialog.hidden = false;
        // Set on the next frame so the opening transition has a start state.
        window.requestAnimationFrame(() => dialog.classList.add('is-open'));
        root.classList.add('mb-lightbox-open');
        closeBtn.focus();
    }

    const close = () => {
        if (!dialog || dialog.hidden) {
            return;
        }

        dialog.classList.remove('is-open');
        root.classList.remove('mb-lightbox-open');

        const finish = () => {
            dialog.hidden = true;
            frame.removeAttribute('src');
        };

        if (stillness.matches) {
            finish();
        } else {
            window.setTimeout(finish, 180);
        }

        if (lastFocused && document.contains(lastFocused)) {
            lastFocused.focus();
        }
    };

    function onKeydown(event) {
        if (!dialog || dialog.hidden) {
            return;
        }

        if (event.key === 'Escape') {
            close();
            return;
        }

        if (gallery.length > 1 && event.key === 'ArrowLeft') {
            show(current - 1);
            return;
        }

        if (gallery.length > 1 && event.key === 'ArrowRight') {
            show(current + 1);
            return;
        }

        // Keep Tab inside the dialog: everything behind it is inert while it is up.
        if (event.key === 'Tab') {
            const focusable = Array.from(dialog.querySelectorAll('button'))
                .filter((el) => !el.hidden && el.offsetParent !== null);

            if (!focusable.length) {
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    }

    const init = () => {
        collect().forEach(wrap);

        // Rebuilt from the buttons still in the document: the ones that belonged to the
        // page just left were removed along with it, so stale entries cannot pile up.
        items = Array.from(document.querySelectorAll('.mb-zoom')).map((trigger) => {
            const img = trigger.querySelector('img');
            return { trigger, img, solo: img.classList.contains('project-thumbnail') };
        }).filter((item) => item.img);

        if (!items.length) {
            return;
        }

        if (!dialog) {
            buildDialog();
        }

        labelDialog();
    };

    window.MB = window.MB || {};
    window.MB.lightbox = { init, close };

    init();
})();
